// src/app/shared/services/socket.service.ts
//
// Singleton Socket.IO client.
// Key fix: socket.on() returns an Observable that re-attaches the listener
// after every reconnect, and never completes due to a timing race.

import {
  Injectable,
  inject,
  PLATFORM_ID,
  OnDestroy,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject, Observable } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../environments/environment';
import { AuthStateService, SessionEndReason } from '../states/auth.state';

@Injectable({ providedIn: 'root' })
export class SocketService implements OnDestroy {

  private platformId = inject(PLATFORM_ID);
  private authState  = inject(AuthStateService);

  private socket: Socket | null = null;

  // ── Public connection status ───────────────────────────────────────────────
  private _connected$    = new Subject<void>();
  private _disconnected$ = new Subject<void>();
  readonly connected$    = this._connected$.asObservable();
  readonly disconnected$ = this._disconnected$.asObservable();

  // ── Pending event subscriptions (created before socket is ready) ───────────
  // Each entry is a factory that attaches a handler to a live socket.
  // On reconnect, we replay all of them.
  private pendingListeners: Array<{
    event:   string;
    handler: (...args: any[]) => void;
  }> = [];

  // ═══════════════════════════════════════════════════════════════════════════
  // Connect / Disconnect
  // ═══════════════════════════════════════════════════════════════════════════

  connect(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Already connected — nothing to do
    if (this.socket?.connected) return;

    // Socket exists but is reconnecting — don't create a second instance
    if (this.socket) return;

    const token = this.authState.getToken();
    if (!token) {
      console.warn('[SocketService] No token — cannot connect');
      return;
    }

    this.socket = io(environment.socketUrl, {
      auth:                { token },
      transports:          ['websocket'],
      reconnection:        true,
      reconnectionDelay:   1_000,
      reconnectionAttempts: 10,
    });

    this.socket.on('connect', () => {
      console.log('[SocketService] Connected:', this.socket?.id);
      // Re-attach any listeners that were registered before connect completed
      this._reattachPendingListeners();
      this._connected$.next();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[SocketService] Disconnected:', reason);
      this._disconnected$.next();
    });

    this.socket.on('reconnect', () => {
      console.log('[SocketService] Reconnected — re-attaching listeners');
      this._reattachPendingListeners();
    });

    // Server-pushed sign-out: this device's session was revoked (password
    // changed/reset on another device, signed out elsewhere) or the account
    // was disabled. The server drops the socket right after sending these.
    this.socket.on('session:revoked', (payload: { reason?: SessionEndReason } | undefined) => {
      this.authState.endSession(payload?.reason ?? 'expired');
    });
    this.socket.on('account:disabled', (payload: { message?: string } | undefined) => {
      this.authState.endSession('disabled', payload?.message);
    });

    this.socket.on('connect_error', (err: Error) => {
      console.error('[SocketService] Connect error:', err.message);
      if (err.message === 'Unauthorized' || err.message === 'AGE_VERIFICATION_REQUIRED') {
        this.disconnect();
      }
    });
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.pendingListeners = [];
  }

  get isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Emit
  // ═══════════════════════════════════════════════════════════════════════════

  emit<T = unknown>(event: string, payload?: T): void {
    if (!this.socket?.connected) {
      console.warn(`[SocketService] Cannot emit "${event}" — not connected`);
      return;
    }
    this.socket.emit(event, payload);
    console.log(`[SocketService] Emitted: ${event}`);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Listen
  //
  // CRITICAL FIX: The Observable defers handler attachment.
  // If the socket isn't ready yet, we register the handler in pendingListeners
  // and attach it the moment the socket connects (or reconnects).
  // This eliminates the race condition between connect() and on() calls.
  // ═══════════════════════════════════════════════════════════════════════════

  on<T = unknown>(event: string): Observable<T> {
    return new Observable<T>(observer => {
      const handler = (data: T) => {
        console.log(`[SocketService] Received: ${event}`);
        observer.next(data);
      };

      // Track so we can re-attach on reconnect
      const entry = { event, handler };
      this.pendingListeners.push(entry);

      // Attach immediately if socket already exists (connected or connecting)
      if (this.socket) {
        this.socket.on(event, handler);
      }
      // If socket doesn't exist yet, it will be attached in _reattachPendingListeners
      // once connect() → 'connect' event fires.

      // Teardown: remove the listener and remove from pending list
      return () => {
        this.socket?.off(event, handler);
        const idx = this.pendingListeners.indexOf(entry);
        if (idx !== -1) this.pendingListeners.splice(idx, 1);
      };
    });
  }

  off(event: string): void {
    this.socket?.off(event);
    this.pendingListeners = this.pendingListeners.filter(l => l.event !== event);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Private
  // ═══════════════════════════════════════════════════════════════════════════

  /** Re-attach all tracked listeners to the current socket instance */
  private _reattachPendingListeners(): void {
    if (!this.socket) return;
    for (const { event, handler } of this.pendingListeners) {
      // Remove first to avoid duplicates, then re-add
      this.socket.off(event, handler);
      this.socket.on(event, handler);
    }
    console.log(`[SocketService] Re-attached ${this.pendingListeners.length} listeners`);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Lifecycle
  // ═══════════════════════════════════════════════════════════════════════════

  ngOnDestroy(): void {
    this.disconnect();
  }
}